// src/utils/calculateNodeNature.ts

import type { StrategyNode } from '../types/BaseNode';
import type { DataNature } from './natureValidation';
import type { DataTypeSpec } from '../hooks/useDataTypes';
import type { FormatterTypeSpec } from '../hooks/useFormatterTypes';

/**
 * Recursively calculates the data nature of any node in the strategy tree
 * 
 * This mirrors the backend logic:
 * - Single nodes: Get nature from DataType
 * - Formatter nodes: Transform nature using formatter's output nature logic
 * - Composite nodes: Determine nature based on operator and child natures
 */
export function calculateNodeNature(
    node: StrategyNode | undefined,
    getDataTypeById: (id: string) => DataTypeSpec | undefined,
    getFormatterTypeById: (id: string) => FormatterTypeSpec | undefined
): DataNature | undefined {
    if (!node) return undefined;

    switch (node.type) {
        case 'Single':
            return calculateSingleNodeNature(node, getDataTypeById);

        case 'Formatter':
            return calculateFormatterNodeNature(node, getDataTypeById, getFormatterTypeById);

        case 'Composite':
            return calculateCompositeNodeNature(node, getDataTypeById, getFormatterTypeById);

        case 'PnL':
            return 'result';

        default:
            return undefined;
    }
}

/**
 * Calculate nature for Single nodes (direct data sources)
 */
function calculateSingleNodeNature(
    node: StrategyNode,
    getDataTypeById: (id: string) => DataTypeSpec | undefined
): DataNature | undefined {
    const config = (node as any).config;
    const typeId = config?.typeId;
    if (!typeId) return undefined;

    const dataType = getDataTypeById(typeId);
    let nature = dataType?.data_nature as DataNature;

    // Manual overrides for Trayport dynamic nature
    if (typeId === 'analytics_trade_summary_fixed' || typeId === 'analytics_trade_summary') {
        const aggType = config?.props?.type;
        if (aggType === 'volume_sum') {
            nature = 'volume';
        } else {
            nature = 'price';
        }
    }

    return nature;
}

/**
 * Calculate nature for Formatter nodes (transformations)
 * 
 * Logic:
 * - Signal-generating formatters (RSI, ZScore, etc.) always return 'signal'
 * - Other formatters inherit the input nature
 */
function calculateFormatterNodeNature(
    node: StrategyNode,
    getDataTypeById: (id: string) => DataTypeSpec | undefined,
    getFormatterTypeById: (id: string) => FormatterTypeSpec | undefined
): DataNature | undefined {
    const config = (node as any).config;
    const formatterId = config?.typeId;  // Fixed: FormatterNode uses 'typeId', not 'formatterId'
    const childNode = config?.child;

    if (!formatterId || !childNode) return undefined;

    // Get the input nature from the child node
    const inputNature = calculateNodeNature(childNode, getDataTypeById, getFormatterTypeById);
    if (!inputNature) return undefined;

    // Check if this formatter transforms the nature
    // These formatters always return 'signal' regardless of input
    const signalGenerators = [
        'rsi',
        'rolling_zscore',
        'rolling_z_score',
        'moving_average_cross',
        'ma_cross',
        'price_ma_cross',
        'moving_average_slope',
        'trendlines',
    ];

    if (signalGenerators.includes(formatterId)) {
        return 'signal';
    }

    // All other formatters inherit the input nature
    return inputNature;
}

/**
 * Calculate nature for Composite nodes (combinations)
 * 
 * Logic matches backend CompositeComponent._determine_result_nature:
 * - Same nature: preserve it
 * - Either is 'signal': result is 'signal'
 * - Either is 'generic': result is the other nature (or 'generic' if both)
 * - Different natures with arithmetic: result is 'generic'
 * - Comparisons: preserve left nature
 */
function calculateCompositeNodeNature(
    node: StrategyNode,
    getDataTypeById: (id: string) => DataTypeSpec | undefined,
    getFormatterTypeById: (id: string) => FormatterTypeSpec | undefined
): DataNature | undefined {
    const config = (node as any).config;
    const leftNode = config?.left;
    const rightNode = config?.right;
    const operator = config?.operator;

    if (!leftNode || !rightNode || !operator) return undefined;

    // Recursively calculate child natures
    const leftNature = calculateNodeNature(leftNode, getDataTypeById, getFormatterTypeById);
    const rightNature = calculateNodeNature(rightNode, getDataTypeById, getFormatterTypeById);

    if (!leftNature || !rightNature) return undefined;

    // Comparison operators preserve left nature
    const comparisonOps = ['GreaterThan', 'LessThan', 'Equal', 'NotEqual'];
    if (comparisonOps.includes(operator)) {
        return leftNature;
    }

    // Filter operators preserve left nature
    const filterOps = ['Filter', 'FilterNot'];
    if (filterOps.includes(operator)) {
        return leftNature;
    }

    // Same nature: preserve it
    if (leftNature === rightNature) {
        return leftNature;
    }

    // If either is 'signal', result is 'signal'
    if (leftNature === 'signal' || rightNature === 'signal') {
        return 'signal';
    }

    // If either is 'generic', result is the other nature (or 'generic' if both)
    if (leftNature === 'generic') {
        return rightNature;
    }
    if (rightNature === 'generic') {
        return leftNature;
    }

    // Different natures: result is 'generic'
    // (e.g., price / volume = generic ratio)
    return 'generic';
}
